/*
 * Workspace onboarding checklist — derived from the workspace's actual
 * state. Each step queries an existing endpoint and reports done /
 * pending. Done steps stay visible (they're proof of progress); pending
 * steps link to the action page.
 */

import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";

interface Step {
  id: string;
  label: string;
  description: string;
  done: boolean;
  action?: { to: string; label: string };
}

export default function OnboardingChecklist() {
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        // Pull each signal in parallel; failed probes count as "step
        // not yet completed" rather than blocking the whole checklist.
        const [
          quotaR,
          dhis2R,
          talliesR,
          indicatorsR,
          jobsR
        ] = await Promise.all([
          api.workspaceDhisInstances().catch(() => null),
          api.workspaceDhisInstances().catch(() => ({ instances: [] as Array<{ enabled: boolean; secretConfigured: boolean }> })),
          api.listTallies().catch(() => ({ tallies: [] })),
          api.listIndicators().catch(() => ({ indicators: [] })),
          api.jobs().catch(() => ({ jobs: [] }))
        ]);

        const dhis2Instances = (dhis2R?.instances ?? []) as Array<{ enabled: boolean; secretConfigured: boolean }>;
        const hasDhis2 = dhis2Instances.length > 0;
        const dhis2Configured = dhis2Instances.some((c) => c.enabled && c.secretConfigured);
        const hasTallies = (talliesR.tallies ?? []).length > 0;
        const hasIndicators = (indicatorsR.indicators ?? []).length > 0;
        const hasSyncJobs = (jobsR.jobs as unknown as Array<{ status: string }>).some((j) => j.status === "completed");
        const hasFailures = (jobsR.jobs as unknown as Array<{ status: string }>).some((j) => j.status === "failed" || j.status === "dead-lettered");

        const _ = quotaR; // currently unused; reserved for plan-detail step

        setSteps([
          {
            id: "dhis2-instance",
            label: "Connect a DHIS2 instance",
            description: "Pair a DHIS2 server so the platform can pull metadata + data.",
            done: hasDhis2,
            action: { to: "/app/administration/connections/instances", label: "Open DHIS2 instances" }
          },
          {
            id: "credentials",
            label: "Configure credentials",
            description: "Store a credential (PAT or OAuth) for at least one instance.",
            done: dhis2Configured,
            action: { to: "/app/administration/connections/instances", label: "Configure credentials" }
          },
          {
            id: "first-sync",
            label: "Run a sync",
            description: "Trigger any sync — metadata, tracker events, or aggregate data.",
            done: hasSyncJobs,
            action: { to: "/app/jobs", label: "Open sync jobs" }
          },
          {
            id: "tallies",
            label: "Generate TallyMetrics",
            description: "Once tracker metadata is synced, TallyMetrics auto-generate.",
            done: hasTallies,
            action: { to: "/app/tallying/tallymetrics", label: "View TallyMetrics" }
          },
          {
            id: "indicators",
            label: "Define an indicator",
            description: "Combine TallyMetrics into an indicator with numerator + denominator.",
            done: hasIndicators,
            action: { to: "/app/tallying/indicators", label: "Open indicators" }
          },
          {
            id: "no-failures",
            label: "Healthy sync state",
            description: "No failed or dead-lettered jobs in the last batch.",
            done: !hasFailures,
            action: { to: "/app/data-activity/failures", label: "Investigate failures" }
          }
        ]);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    })();
  }, []);

  const doneCount = steps ? steps.filter((s) => s.done).length : 0;
  const total = steps?.length ?? 0;

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Onboarding checklist</h1>
          <p className="muted">
            Steps every workspace runs through to go from signed up → indicators publishing.
            Each step checks live workspace state, so the list reflects exactly where you are.
          </p>
        </div>
        {steps && (
          <div className="actions">
            <span className={`pill pill-${doneCount === total ? "ok" : "info"}`}>{doneCount} / {total}</span>
          </div>
        )}
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {!steps && (
        <ol className="onboarding-list">
          {Array.from({ length: 6 }).map((_, i) => (
            <li key={i} className="onboarding-step">
              <div className="onboarding-step-num">·</div>
              <div className="onboarding-step-body">
                <div className="onboarding-step-label"><span className="skeleton skeleton-text" style={{ width: "55%" }} /></div>
                <div className="onboarding-step-desc"><span className="skeleton skeleton-text" style={{ width: "80%" }} /></div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {steps && (
        <ol className="onboarding-list">
          {steps.map((s, i) => (
            <li key={s.id} className={s.done ? "onboarding-step onboarding-step-done" : "onboarding-step"}>
              <div className="onboarding-step-num">{s.done ? "✓" : i + 1}</div>
              <div className="onboarding-step-body">
                <div className="onboarding-step-label">{s.label}</div>
                <div className="onboarding-step-desc">{s.description}</div>
                {!s.done && s.action && (
                  <Link to={s.action.to} className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}>
                    {s.action.label} →
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
